import { call, put, select, takeLatest, take } from "redux-saga/effects";
import {
  getBankDeposit,
  getBankDepositSuccess,
  getBankDepositFailure,
  getStructureNote,
  getStructureNoteSuccess,
  getStructureNoteFailure,
  getBond,
  getBondSuccess,
  getBondFailure,
  getFundByUnitholder,
  getFundByUnitholderSuccess,
  getFundByUnitholderFailure,
  getUnitholders,
  getUnitholdersSuccess,
  getUnitholdersFailure,
  getFund,
  getFundSuccess,
  getFundFailure,
  getFundWithUnitholders,
  setMutualFundLoading,
  viewSpecificFundType,
  viewSpecificFundTypeSuccess,
  viewSpecificFundTypeFailure,
  viewTransactionHistory,
  viewTransactionHistorySuccess,
  viewTransactionHistoryFailure,
  getCustomerCareContact,
  getCustomerCareContactSuccess,
  getCustomerCareContactFailure,
} from "redux/reducers/portfolio";
import { PortfolioSelector } from "redux/selector/portfolio-selector";
import { PortfolioAPI } from "services/portfolio";

function* getCustomerCareContactSaga({ payload }) {
  try {
    const response = yield call(PortfolioAPI.getCustomerCareContact, payload?.signal);
    yield put(getCustomerCareContactSuccess(response.data.employeeContactInfo));
  } catch (error) {
    yield put(getCustomerCareContactFailure(error));
  }
}

function* getBankDepositSaga({ payload }) {
  try {
    const response = yield call(PortfolioAPI.getBankDeposit, payload?.signal);
    yield put(
      getBankDepositSuccess({
        bankDeposit: response.data.bankDeposit,
      })
    );
  } catch (error) {
    yield put(getBankDepositFailure(error));
  }
}

function* getFundByUnitholderSaga({ payload }) {
  try {
    const filterUnitholder = yield select(PortfolioSelector.filterUnitholder);
    const currencyTypeSelected = yield select(PortfolioSelector.currencyTypeSelected);
    const currentCriteries = yield select(PortfolioSelector.mutualFundCriteries);
    const unitholders = yield select(PortfolioSelector.unitholders);
    const criteries = {
      unitholder: filterUnitholder?.join(",") || null,
      currencyType: currencyTypeSelected || null,
    };
    const assumeAllSelectUnitholderLikeNullFilter =
      currentCriteries?.unitholder === null &&
      criteries.unitholder === unitholders.map((u) => u.unitholderNo).join(","); // select all
    const sameUnitholder =
      criteries.unitholder === currentCriteries?.unitholder || assumeAllSelectUnitholderLikeNullFilter;
    const sameCurrencyType = criteries.currencyType === (currentCriteries?.currencyType ?? null);
    if (!sameUnitholder || !sameCurrencyType) {
      yield put(setMutualFundLoading(true));
      const response = yield call(PortfolioAPI.getFundByUnitholder, criteries, payload?.signal);
      const result = response.data.mutualFund ?? {};
      yield put(
        getFundByUnitholderSuccess({
          ...result,
          criteries,
        })
      );
    } else {
      yield put(setMutualFundLoading(false));
    }
  } catch (error) {
    yield put(getFundByUnitholderFailure(error));
  }
}

function* viewSpecificFundTypeSaga({ payload }) {
  try {
    const filterUnitholder = yield select(PortfolioSelector.filterUnitholder);
    const fundTypeSelected = yield select(PortfolioSelector.fundTypeSelected);
    const currencyTypeSelected = yield select(PortfolioSelector.currencyTypeSelected);
    const criteries = {
      unitholder: filterUnitholder?.join(",") || null,
      mfTypeCode: fundTypeSelected.mfTypeCode,
      mfTypeName: fundTypeSelected.mfTypeName,
      currencyType: currencyTypeSelected,
    };
    const response = yield call(PortfolioAPI.getMutualFundListByType, criteries, payload?.signal);
    yield put(viewSpecificFundTypeSuccess(response.data.mutualFundList));
  } catch (error) {
    yield put(viewSpecificFundTypeFailure(error));
  }
}

function* viewTransactionHistorySaga({ payload }) {
  try {
    const fundSelected = yield select(PortfolioSelector.fundSelected);
    const criteries = {
      unitholder: fundSelected.unitHolder,
      fundCode: fundSelected.fundCode,
    };
    const response = yield call(PortfolioAPI.getFundTransactions, criteries, payload?.signal);
    yield put(viewTransactionHistorySuccess(response.data.mutualFundTransaction));
  } catch (error) {
    yield put(viewTransactionHistoryFailure(error));
  }
}

function* getBondSaga({ payload }) {
  try {
    const response = yield call(PortfolioAPI.getBond, payload?.signal);
    yield put(getBondSuccess(response.data.bond));
  } catch (error) {
    yield put(getBondFailure(error));
  }
}

function* getFundSaga({ payload }) {
  try {
    const filterUnitholder = yield select(PortfolioSelector.filterUnitholder);
    const currencyTypeSelected = yield select(PortfolioSelector.currencyTypeSelected);
    const criteries = {
      unitholder: payload?.unitholder ?? (filterUnitholder?.join(",") || null),
      currencyType: payload?.currencyType ?? currencyTypeSelected ?? null,
    };
    const response = yield call(PortfolioAPI.getFundByUnitholder, criteries, payload?.signal);
    yield put(getFundSuccess({ ...(response.data.mutualFund ?? {}), criteries }));
  } catch (error) {
    yield put(getFundFailure(error));
  }
}

function* getStructureNoteSaga({ payload }) {
  try {
    const response = yield call(PortfolioAPI.getStructureNote, payload?.signal);
    yield put(
      getStructureNoteSuccess({
        structureNote: response.data.structureNote,
      })
    );
  } catch (error) {
    yield put(getStructureNoteFailure(error));
  }
}

function* getUnitholdersSaga({ payload }) {
  try {
    const response = yield call(PortfolioAPI.getUnitholders, payload?.signal);
    yield put(getUnitholdersSuccess(response.data.unitholder?.unitholderList || []));
  } catch (error) {
    yield put(getUnitholdersFailure(error));
  }
}

function* getFundWithUnitholdersSaga({ payload }) {
  try {
    yield put(getUnitholders({ signal: payload?.signal }));
    yield take(getUnitholdersSuccess.type);
    const unitholders = yield select(PortfolioSelector.unitholders);
    const allCurrencyLists =
      Array.isArray(unitholders) && unitholders.length > 0
        ? unitholders
          .filter((u) => Array.isArray(u.currencyList))
          .flatMap((u) => u.currencyList.map((c) => c && c.toUpperCase()).filter(Boolean))
        : [];
    const hasTHB = allCurrencyLists.includes("THB");
    const isAllTHBOnly = allCurrencyLists.length > 0 && allCurrencyLists.every((c) => c === "THB");
    const isAllFCYOnly = allCurrencyLists.length > 0 && !hasTHB;
    const currencyType = isAllTHBOnly ? "THB" : isAllFCYOnly ? "FCY" : null;
    yield put(
      getFund({
        signal: payload?.signal,
        currencyType,
      })
    );
  } catch (error) {
    console.log(error);
    yield put(getFundFailure(error));
  }
}

const saga = [
  takeLatest(getCustomerCareContact.type, getCustomerCareContactSaga),
  takeLatest(getBankDeposit.type, getBankDepositSaga),
  takeLatest(getFund.type, getFundSaga),
  takeLatest(getFundWithUnitholders.type, getFundWithUnitholdersSaga),
  takeLatest(getBond.type, getBondSaga),
  takeLatest(getFundByUnitholder.type, getFundByUnitholderSaga),
  takeLatest(getStructureNote.type, getStructureNoteSaga),
  takeLatest(getUnitholders.type, getUnitholdersSaga),
  takeLatest(viewSpecificFundType.type, viewSpecificFundTypeSaga),
  takeLatest(viewTransactionHistory.type, viewTransactionHistorySaga),
];

export default saga;
